import React from 'react';
import { ChevronDown } from 'lucide-react';

interface HeroProps {
  title: string;
  subtitle: string;
  children?: React.ReactNode;
  variant?: 'default' | 'compact';
  ctaLabel?: string;
  ctaHref?: string;
  backgroundImageUrl?: string;
  icon?: React.ReactElement;
  iconLabel?: string;
}

const Hero: React.FC<HeroProps> = ({ title, subtitle, children, variant = 'default', ctaLabel, ctaHref, backgroundImageUrl, icon, iconLabel }) => {
  const isCompact = variant === 'compact';

  return (
    <section className={`relative overflow-hidden ${backgroundImageUrl ? '' : 'hero-pattern'} ${isCompact ? 'pt-28 pb-10' : 'pt-32 pb-20'} px-4`}>
      {backgroundImageUrl && (
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url("${backgroundImageUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        ></div>
      )}
      <div className={`absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-700 to-primary-500 ${backgroundImageUrl ? 'opacity-80' : ''}`}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-400 rounded-full blur-3xl animate-pulse-soft"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary-300 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>

      <div className="container mx-auto text-center relative z-10">
        <div className={`max-w-4xl mx-auto glass-effect ${isCompact ? 'p-6 md:p-8' : 'p-8 md:p-12'} rounded-3xl shadow-glass animate-fade-in-up`}>
          {icon && iconLabel && (
            <div className="flex items-center justify-center mb-6">
              <div className="bg-primary p-3 rounded-2xl shadow-lg mr-3">
                {React.cloneElement(icon, { className: 'h-8 w-8 text-white' })}
              </div>
              <span className="text-2xl font-bold text-white">{iconLabel}</span>
            </div>
          )}
          <h1 className={`${isCompact ? 'text-3xl md:text-5xl mb-4' : 'text-4xl md:text-6xl mb-6'} font-heading font-bold text-white drop-shadow-lg`}>
            {title}
          </h1>
          <p className={`${isCompact ? 'text-lg md:text-xl mb-6' : 'text-xl md:text-2xl mb-8'} text-white/85 font-light leading-relaxed`}>
            {subtitle}
          </p>
          {ctaLabel && ctaHref && (
            <a href={ctaHref} className="btn-accent inline-block text-lg mb-6">
              {ctaLabel}
            </a>
          )}
          {children}
        </div>
      </div>

      {!isCompact && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:block">
          <ChevronDown className="h-6 w-6 text-white/50 animate-bounce" />
        </div>
      )}
    </section>
  );
};

export default Hero;
